import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import NearMeIcon from '@mui/icons-material/NearMe';
import GestureIcon from '@mui/icons-material/Gesture';
import StraightenIcon from '@mui/icons-material/Straighten';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  currentUserPreferencesSelector,
  isStaffSelector,
  updatePreferences,
} from 'store/slices/userDetailsSlice';
import {
  ANNOTATION_COLOR_FIELD,
  ANNOTATION_COLOR_PALETTE,
  ANNOTATION_DRAW_MODE_ENABLED_FIELD,
  ANNOTATION_DRAW_TOOL_FIELD,
  ANNOTATION_STROKE_WIDTH_FIELD,
  ANNOTATIONS_SWITCH_FIELD,
  DEFAULT_ANNOTATION_COLOR,
  DEFAULT_ANNOTATION_STROKE_WIDTH,
  DISTANCE_MEASURE_ENABLED_FIELD,
} from 'components/ImageViewer/constants';
import { useViewerMode, VIEWER_MODES } from 'components/ImageViewer/hooks';
import { ColorProfileMenu } from 'components/ImageViewer/components/ImageViewerToolbar/ColorProfileMenu';
import { DrawingToolsButtonGroup } from 'components/ImageViewer/components/ImageViewerToolbar/Buttons/DrawingToolsButtonGroup';
import { useViewerModeRailStyles } from 'components/ImageViewer/components/ViewerModeRail/styles';

const DRAG_PADDING = 4;

/**
 * The rail sits on the right edge by default; once dragged, whichever half of
 * the viewer it is in decides where the flyout and tooltips open so they stay
 * over the slide.
 */
export const isRailOnRightSide = (position) =>
  position ? position.xPct > 0.5 : true;

export const ViewerModeRail = ({ annotationsCount = 0 }) => {
  const preferences = useSelector(currentUserPreferencesSelector);
  // Viewer Presets is staff-only, so its slot and divider are too.
  const isStaff = useSelector(isStaffSelector);
  const dispatch = useDispatch();
  const { mode, setMode, setDrawTool, setDrawColor, setDrawStrokeWidth } =
    useViewerMode();
  const { classes, cx } = useViewerModeRailStyles();

  const isAnnotationsEnabled = !!preferences[ANNOTATIONS_SWITCH_FIELD];

  // Showing/hiding annotations is a per-session choice (not persisted),
  // like the toolbar switch it replaces. Hiding them also leaves any tool.
  const handleAnnotationsToggle = () => {
    dispatch(
      updatePreferences({
        [ANNOTATIONS_SWITCH_FIELD]: !isAnnotationsEnabled,
        ...(isAnnotationsEnabled && {
          [ANNOTATION_DRAW_MODE_ENABLED_FIELD]: false,
          [DISTANCE_MEASURE_ENABLED_FIELD]: false,
        }),
      }),
    );
  };

  const railRef = useRef(null);
  const dragRef = useRef(null);
  // Where the rail was dragged to during this visit. Nothing is persisted:
  // every slide opens with the rail at the right edge, vertically centred.
  const [position, setPosition] = useState(null);

  const isDrawMode = mode === VIEWER_MODES.DRAW;
  const isMeasureMode = mode === VIEWER_MODES.MEASURE;
  const isOnRightSide = isRailOnRightSide(position);
  const tooltipPlacement = isOnRightSide ? 'left' : 'right';
  const selectedColor =
    preferences[ANNOTATION_COLOR_FIELD] || DEFAULT_ANNOTATION_COLOR;
  const strokeWidth =
    preferences[ANNOTATION_STROKE_WIDTH_FIELD] ||
    DEFAULT_ANNOTATION_STROKE_WIDTH;
  const [strokeWidthDraft, setStrokeWidthDraft] = useState(strokeWidth);

  useEffect(() => {
    setStrokeWidthDraft(strokeWidth);
  }, [strokeWidth]);

  /** The rail moves inside its viewer container (`.ocd-div`). */
  const getBounds = () => {
    const rail = railRef.current;
    const container = rail?.offsetParent;

    if (!rail || !container) {
      return null;
    }

    return {
      rail: rail.getBoundingClientRect(),
      container: container.getBoundingClientRect(),
    };
  };

  const handleGripPointerDown = (event) => {
    const bounds = getBounds();

    if (!bounds) {
      return;
    }

    event.preventDefault();

    const drag = {
      offsetX: event.clientX - bounds.rail.left,
      offsetY: event.clientY - bounds.rail.top,
      railWidth: bounds.rail.width,
      railHeight: bounds.rail.height,
      containerLeft: bounds.container.left,
      containerTop: bounds.container.top,
      containerWidth: bounds.container.width,
      containerHeight: bounds.container.height,
    };

    dragRef.current = drag;

    const handleDragMove = (moveEvent) => {
      const maxLeft = Math.max(
        DRAG_PADDING,
        drag.containerWidth - drag.railWidth - DRAG_PADDING,
      );
      const maxTop = Math.max(
        DRAG_PADDING,
        drag.containerHeight - drag.railHeight - DRAG_PADDING,
      );

      const left = Math.min(
        Math.max(
          moveEvent.clientX - drag.offsetX - drag.containerLeft,
          DRAG_PADDING,
        ),
        maxLeft,
      );
      const top = Math.min(
        Math.max(
          moveEvent.clientY - drag.offsetY - drag.containerTop,
          DRAG_PADDING,
        ),
        maxTop,
      );

      setPosition({
        xPct: left / drag.containerWidth,
        yPct: top / drag.containerHeight,
      });
    };

    const handleDragEnd = () => {
      window.removeEventListener('pointermove', handleDragMove, true);
      window.removeEventListener('pointerup', handleDragEnd, true);
      window.removeEventListener('pointercancel', handleDragEnd, true);

      dragRef.current = null;
    };

    window.addEventListener('pointermove', handleDragMove, true);
    window.addEventListener('pointerup', handleDragEnd, true);
    window.addEventListener('pointercancel', handleDragEnd, true);
  };

  const resetPosition = () => setPosition(null);

  // Each mode is an icon plus a one-word caption, so the rail reads without
  // hovering for the tooltip.
  const modes = [
    {
      value: VIEWER_MODES.SELECT,
      title: 'Select — click an annotation to edit it',
      caption: 'Select',
      Icon: NearMeIcon,
      isSelected: mode === VIEWER_MODES.SELECT,
      onClick: () => setMode(VIEWER_MODES.SELECT),
    },
    {
      value: VIEWER_MODES.DRAW,
      title: 'Draw annotations — square, circle or pencil',
      caption: 'Draw',
      Icon: GestureIcon,
      isSelected: isDrawMode,
      // Clicking Draw again drops back to Select and closes the flyout.
      onClick: () =>
        setMode(isDrawMode ? VIEWER_MODES.SELECT : VIEWER_MODES.DRAW),
    },
    {
      value: VIEWER_MODES.MEASURE,
      title: 'Measure distance — click two points',
      caption: 'Measure',
      Icon: StraightenIcon,
      isSelected: isMeasureMode,
      onClick: () => setMode(VIEWER_MODES.MEASURE),
    },
  ];

  return (
    <Box
      ref={railRef}
      className={classes.rail}
      role="toolbar"
      aria-label="Annotation modes"
      style={
        position
          ? {
            left: `${position.xPct * 100}%`,
            right: 'auto',
            top: `${position.yPct * 100}%`,
            transform: 'none',
          }
          : undefined
      }
    >
      <Box
        className={classes.grip}
        title="Drag to move the annotation toolbar"
        aria-label="Drag to move the annotation toolbar"
        onPointerDown={handleGripPointerDown}
        onDoubleClick={resetPosition}
      >
        <DragHandleIcon fontSize="inherit" />
      </Box>

      {isStaff && (
        <>
          <ColorProfileMenu
            className={classes.presetsButton}
            tooltipPlacement={tooltipPlacement}
            menuSide={isOnRightSide ? 'left' : 'right'}
          />
          <Box className={classes.divider} />
        </>
      )}

      <Tooltip
        title={
          isAnnotationsEnabled
            ? 'Annotations shown — click to hide'
            : 'Annotations hidden — click to show'
        }
        placement={tooltipPlacement}
      >
        <IconButton
          className={cx(
            classes.annotationsToggle,
            isAnnotationsEnabled && classes.annotationsToggleOn,
          )}
          onClick={handleAnnotationsToggle}
          aria-pressed={isAnnotationsEnabled}
          aria-label={
            isAnnotationsEnabled ? 'Hide annotations' : 'Show annotations'
          }
          size="small"
        >
          <Badge
            badgeContent={isAnnotationsEnabled ? annotationsCount : 0}
            max={999}
            color="primary"
            overlap="circular"
            classes={{ badge: classes.annotationsBadge }}
          >
            {isAnnotationsEnabled ? (
              <VisibilityIcon fontSize="small" />
            ) : (
              <VisibilityOffIcon fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {isAnnotationsEnabled && <Box className={classes.divider} />}

      {isAnnotationsEnabled &&
        modes.map(({ value, title, caption, Icon, isSelected, onClick }) => (
          <Tooltip key={value} title={title} placement={tooltipPlacement}>
            <ToggleButton
              value={value}
              aria-label={value}
              selected={isSelected}
              onClick={onClick}
              className={classes.modeButton}
            >
              <Icon className={classes.modeIcon} fontSize="inherit" />
              <span className={classes.modeCaption}>{caption}</span>
            </ToggleButton>
          </Tooltip>
        ))}

      {isAnnotationsEnabled && isDrawMode && (
        <Box
          className={cx(
            classes.shapeFlyout,
            isOnRightSide ? classes.shapeFlyoutLeft : classes.shapeFlyoutRight,
          )}
          data-side={isOnRightSide ? 'left' : 'right'}
        >
          <DrawingToolsButtonGroup
            handleDrawerToolChange={(event, tool) => {
              if (tool) {
                setDrawTool(tool);
              }
            }}
            selectedDrawTool={preferences[ANNOTATION_DRAW_TOOL_FIELD]}
          />

          <Box className={classes.flyoutSection}>
            <Typography variant="caption" className={classes.flyoutLabel}>
              Color
            </Typography>
            <Box className={classes.swatches}>
              {ANNOTATION_COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`annotation color ${color}`}
                  title={color}
                  className={`${classes.swatch}${
                    color === selectedColor ? ` ${classes.swatchSelected}` : ''
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setDrawColor(color)}
                />
              ))}
            </Box>
          </Box>

          <Box className={classes.flyoutSection}>
            <Typography variant="caption" className={classes.flyoutLabel}>
              Stroke
            </Typography>
            <Slider
              size="small"
              min={1}
              max={8}
              step={1}
              value={strokeWidthDraft}
              aria-label="annotation stroke width"
              onChange={(event, value) => setStrokeWidthDraft(value)}
              onChangeCommitted={(event, value) => setDrawStrokeWidth(value)}
              className={classes.strokeSlider}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
